/**
 * Opengater user-card hooks (users service, `admin: users`).
 *
 * `{user_id}` is the users-service INTEGER id. The card is opened by id; a
 * typeahead resolves an id via the list endpoint's universal `search`
 * (see `useOgUserSearch`). The three mutations each refetch the card on success
 * so the view reflects the server (device number, effective max-devices, sub).
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, } from '@tanstack/react-query';
import { apiOg } from './client';
const BASE = '/api/v1/admin/users';
const cardKey = (id) => ['og', 'user', id];
const subscriptionKey = (id) => ['og', 'user', id, 'subscription'];
export function useOgUserCard(userId) {
    return useQuery({
        queryKey: cardKey(userId),
        queryFn: () => apiOg('users', `${BASE}/${userId}`),
        enabled: userId != null,
        retry: false, // a 404 "user not found" shouldn't retry 3× before surfacing
    });
}
export const USER_PAGE = 20;
/**
 * Paginated universal search over the users list, as `useInfiniteQuery` so the
 * support screen can "load more" and client-filter across the loaded rows. An
 * empty query returns the newest users (the endpoint's default full list).
 */
export function useOgUserSearch(query) {
    const q = query.trim();
    return useInfiniteQuery({
        queryKey: ['og', 'user-search', q],
        queryFn: ({ pageParam }) => apiOg('users', `${BASE}?search=${encodeURIComponent(q)}&limit=${USER_PAGE}&offset=${pageParam}`),
        initialPageParam: 0,
        getNextPageParam: (last, pages) => {
            const loaded = pages.reduce((n, p) => n + p.items.length, 0);
            return loaded < last.total ? loaded : undefined;
        },
        placeholderData: keepPreviousData,
        staleTime: 15_000,
        retry: false,
    });
}
export function useSetDeviceNumber(userId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', `${BASE}/${userId}/device-number`, {
            method: 'PATCH',
            body,
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: cardKey(userId) }),
    });
}
export function useSetMaxDevicesOverride(userId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', `${BASE}/${userId}/max-devices-override`, {
            method: 'PATCH',
            body,
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: cardKey(userId) }),
    });
}
export function useSetEmailMailings(userId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', `${BASE}/${userId}/email-mailings`, {
            method: 'PUT',
            body,
        }),
        onSuccess: (card) => qc.setQueryData(cardKey(userId), card),
    });
}
export function useRevokeSubscription(userId) {
    const qc = useQueryClient();
    return useMutation({
        // Re-issue (202, empty body): issues a NEW subscription link and breaks the
        // configs already installed on the user's devices. Async — the new link lands
        // in the card a moment later, so refetch after a short delay, not immediately.
        mutationFn: () => apiOg('users', `${BASE}/${userId}/subscription/revoke`, { method: 'POST' }),
        onSuccess: () => {
            setTimeout(() => void qc.invalidateQueries({ queryKey: subscriptionKey(userId) }), 3000);
        },
    });
}
export function useOgUserSubscription(userId, enabled) {
    return useQuery({
        queryKey: subscriptionKey(userId),
        queryFn: () => apiOg('users', `${BASE}/${userId}/subscription`),
        enabled,
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}
export function useOgUserPayments(userId, enabled) {
    return useQuery({
        queryKey: ['og', 'user', userId, 'payments'],
        queryFn: () => apiOg('users', `${BASE}/${userId}/payments?limit=50&offset=0`),
        enabled,
        retry: false,
    });
}
export function useOgUserInvitees(userId, enabled) {
    return useQuery({
        queryKey: ['og', 'user', userId, 'invitees'],
        queryFn: () => apiOg('users', `${BASE}/${userId}/invitees?limit=50&offset=0`),
        enabled,
        retry: false,
    });
}
// `/{id}/locations` is the WHOLE catalog, not the user's list: what belongs to
// the user is marked on the options (`is_selected`) — one location can carry
// several options. Read-only: only the user changes their set.
/** The user's own set: locations with a selected option, trimmed to those options. */
export function selectedLocations(data) {
    const pick = (locs) => locs
        .map((l) => ({ ...l, options: l.options.filter((o) => o.is_selected) }))
        .filter((l) => l.options.length > 0);
    return {
        groups: data.groups
            .map((g) => ({ ...g, locations: pick(g.locations) }))
            .filter((g) => g.locations.length > 0),
        locations: pick(data.locations),
    };
}
export function useOgUserLocations(userId, enabled) {
    return useQuery({
        queryKey: ['og', 'user', userId, 'locations'],
        queryFn: () => apiOg('users', `${BASE}/${userId}/locations`),
        enabled,
        retry: false,
    });
}
export function useOgUserTariff(userId, enabled) {
    return useQuery({
        queryKey: ['og', 'user', userId, 'tariff'],
        queryFn: () => apiOg('users', `${BASE}/${userId}/tariff`),
        enabled,
        retry: false,
    });
}
